import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { Link } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import { currencyCode } from 'Helpers/Geo/data';
import BundleCardType from 'Helpers/Product/BundleCardType';
import BundleProductCardCover from 'Components/Utility/BundleProductCardCover';
import DlcBadge from 'Components/Cards/DlcBadge';
import imageSizes from 'Components/Cards/image-sizes';
import Drm from 'Components/Utility/Drm';
import Price from 'Components/Utility/Price/Price';

import 'Features/Bundle/components/Cards/BundleProductCard.scss';

export default function BundleProductCard({
  product,
  switchCarouselGame,
  bigCard,
  sizesOverride,
  bundleSlug,
}) {
  let productDownloads = [];
  if (product.downloads) {
    productDownloads = product.downloads.filter(
      download => !download.is_sample,
    );
  }

  const downloadTypes = [];
  productDownloads.forEach((download) => {
    download.files.forEach((file) => {
      if (includes(downloadTypes, file.format)) return;
      if (file.format === 'zip' || file.format === 'mixed') return;
      downloadTypes.push(file.format);
    });
  });

  // This has been added for the Soundtrack Loops Bundle where a
  // group download was added as a single download
  if (isEmpty(downloadTypes) && bundleSlug === 'soundtrack-loops-bundle') {
    downloadTypes.push('wav');
  }
  if (isEmpty(downloadTypes) && bundleSlug === 'dark-fantasy-royalty-free-music-and-sfx-bundle') {
    downloadTypes.push('mp3');
  }
  if (isEmpty(downloadTypes) && bundleSlug === 'big-royalty-free-game-music-and-sounds-bundle') {
    downloadTypes.push('flac');
  }

  let extraGameDescription = false;
  if (product.customDesc && window.locale === 'en') {
    extraGameDescription = true;
  }

  const validDrm = Object.keys(product.drm || {}).filter(d => product.drm[d]);

  const cardType = BundleCardType(product.type, product.display_type, product.cover_type);

  const showLabel = product.newToBundle || product.preorder_delivery;
  let labelCssName = '';
  if (product.newToBundle) labelCssName = 'new-to-bundle-label';
  if (product.preorder_delivery) labelCssName = 'low-stock-label';

  const modalDetails = _get(product, 'charity.active');

  let showValue = true;
  if (bundleSlug === 'runescape-siren-bundle-limited-edition') showValue = false;

  return (
    <article
      className={classNames(
        cardType,
        { 'big-bundle-card': bigCard },
        { 'card-extra-info': extraGameDescription },
        'faux-block-link',
      )}
    >
      {product.display_type === 'comic' && productDownloads.length > 1 &&
        <div className="comic-issues-label">
          {productDownloads.length}
        </div>
      }

      {product.display_type === 'comic' && product.files > 1 &&
        <div className="comic-issues-label">
          {product.files}
        </div>
      }

      {(product.type === 'audio' || product.type === 'video') && product.files > 1 &&
        <div className="audio-files-label">
          <FormattedMessage
            id="bundle-product-card.label.files"
            defaultMessage="{files} Files"
            values={{
              files: product.files,
            }}
          />
        </div>
      }

      {showLabel &&
        <div className={`card-label ${labelCssName}`}>
          <div className="label-container">
            <div className="label-content">
              {product.newToBundle &&
                <FormattedMessage id="bundle-product-card.label.new-to-bundle" defaultMessage="New to bundle" />
              }
              {product.preorder_delivery &&
                <FormattedMessage id="bundle-product-card.label.preorder" defaultMessage="Keys pending" />
              }
            </div>
            <div className="label-slash" />
          </div>
          <div className="label-triangle" />
        </div>
      }

      <div className={classNames('cover-container', { 'landscape-cover': product.cover_type === 'landscape' })}>
        <BundleProductCardCover
          cover={product.cover}
          name={product.name}
          type={product.type}
          displayType={product.display_type}
          coverType={product.cover_type}
          sizesOverride={sizesOverride}
        />
      </div>

      <div className="card-content">
        <div className="card-bottom-container">
          {bigCard && product.seo && product.seo.desc &&
            <div className="d-none d-md-block">
              <div className="game-description d-none d-md-block">
                <p>{product.seo.desc}</p>
              </div>
            </div>
          }
          {extraGameDescription &&
            <div className="game-description">
              <p>{product.customDesc}</p>
            </div>
          }
          <div className="card-icons-price-container">
            <div className="card-icons-container">
              <div className="platform-container">
                {product.platforms && product.platforms.windows &&
                  <span>
                    <FontAwesomeIcon icon={fabFaWindows} className="mr-2" />
                  </span>
                }
                {product.platforms && product.platforms.mac &&
                  <span>
                    <FontAwesomeIcon icon={fabFaApple} className="mr-2" />
                  </span>
                }
                {product.platforms && product.platforms.linux &&
                  <span>
                    <FontAwesomeIcon icon={fabFaLinux} className="mr-2" />
                  </span>
                }
              </div>
              <div className="drm-dlc-container">
                {!isEmpty(validDrm) &&
                  <Drm drmType={validDrm[0]} />
                }
                {(product.type === 'dlc' || product.display_type === 'dlc') &&
                  <DlcBadge />
                }
              </div>
              <div className="downloads-container">
                {downloadTypes.sort().map(file =>
                  <div key={file} className="comic-download mr-2">
                    {file}
                  </div>,
                )}
              </div>
            </div>
            {showValue &&
              <div className="d-none d-sm-block">
                {product.price && product.price[currencyCode] !== 0 &&
                  <div className="game-price">
                    <p>
                      <FormattedMessage id="bundle-product-card.value" defaultMessage="Value:" />
                      &nbsp;
                      <Price
                        price={product.price}
                      />
                    </p>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div className="overlay-container">
        <div className="card-overlay">
          <div>
            <h3 className="card-product-name">
              {product.name}
            </h3>

            <Link
              spy
              smooth
              offset={-120}
              activeClass="active"
              to={modalDetails ? '' : 'carousel'}
              duration={500}
              className="faux-block-link__overlay-link"
              onClick={() => { switchCarouselGame(product.totalGameIndex); }}
            >
              <p className="product-details">
                <FormattedMessage id="bundle-product-card.detailslink" defaultMessage="Product details" />
              </p>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

BundleProductCard.propTypes = {
  product: PropTypes.shape({}),
  switchCarouselGame: PropTypes.func,
  bigCard: PropTypes.bool,
  sizesOverride: PropTypes.string,
  bundleSlug: PropTypes.string,
};

BundleProductCard.defaultProps = {
  product: {},
  currencyCode: 'GBP',
  switchCarouselGame: () => { },
  bigCard: false,
  sizesOverride: imageSizes(3, 4, 6, 6),
  bundleSlug: '',
};
